// Ability catalog for Nexus Forge combat (Phase 5 Step 6).
//
// Data-driven: adding a new ability is a config change here. Precedent
// is shared/recipes.ts — same shape (frozen entries, map + ordered list,
// `getAbility`, `meetsAbilityRequirements` for the skill+gear gate).
//
// Combat itself (tick loop, hit resolution, cooldown bookkeeping) lands
// in Step 9 on ExpeditionDO. This file is data + a pure predicate only.
// No server or client code here.
//
// Open design call #4 (from CLAUDE.md) resolved to **strict gear**:
// if `gearRequired` is non-null, the equipped weapon's `weaponType`
// must match exactly. Concrete weapon items with a matching weaponType
// land in shared/items.ts at Step 10.

import type { SkillId } from "./skills.ts";

// -----------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------

export const ABILITY_IDS = [
  "basic-attack",
  "basic-shot",
  "heavy-strike",
  "quick-shot",
  "defensive-stance",
  "heal-self",
] as const;
export type AbilityId = (typeof ABILITY_IDS)[number];

// 'melee' / 'ranged' are category tags the equipped weapon must match;
// null means the ability does not require a weapon (buffs, heals).
export type WeaponCategory = "melee" | "ranged";

export type AbilitySkillGate = {
  readonly skillId: SkillId;
  readonly minXp: number;
};

export type AbilityDef = {
  readonly id: AbilityId;
  readonly name: string;
  readonly cooldownMs: number;
  // Mean damage per hit. Negative for heals (heal-self resolves as
  // `damage < 0 → self.hp -= damage` in Step 9). Zero for pure buffs.
  readonly damageBase: number;
  // Uniform jitter: damage ∈ [damageBase - damageVariance, damageBase + damageVariance].
  readonly damageVariance: number;
  // Squared distance cap; 0 = self-targeted / area (no range check).
  readonly range: number;
  readonly gearRequired: WeaponCategory | null;
  readonly skillGate: AbilitySkillGate | null;
};

// -----------------------------------------------------------------------
// Catalog entries — values pre-locked by reviewer (Step 6 brief).
// Playtest rebalance happens later; changes land here, not in balance.ts,
// because these are data-catalog values per the shared/recipes.ts
// / shared/npc-items.ts precedent (not cross-item tuning knobs).
// -----------------------------------------------------------------------

// Every numeric literal below is a data-catalog value (per Phase 5
// Step 6 brief — cooldowns, damage, range, and skill-gate thresholds
// do NOT belong in balance.ts, same rule as recipes.ts input counts).
// The `contracts:allow-literal` markers document that intent inline.

const basicAttack: AbilityDef = {
  id: "basic-attack",
  name: "Basic Attack",
  cooldownMs: 1000, // contracts:allow-literal — ability catalog datum
  damageBase: 5, // contracts:allow-literal — ability catalog datum
  damageVariance: 2, // contracts:allow-literal — ability catalog datum
  range: 80, // contracts:allow-literal — ability catalog datum
  gearRequired: "melee",
  skillGate: null,
};

const basicShot: AbilityDef = {
  id: "basic-shot",
  name: "Basic Shot",
  cooldownMs: 1000, // contracts:allow-literal — ability catalog datum
  damageBase: 4, // contracts:allow-literal — ability catalog datum
  damageVariance: 2, // contracts:allow-literal — ability catalog datum
  range: 250, // contracts:allow-literal — ability catalog datum
  gearRequired: "ranged",
  skillGate: null,
};

const heavyStrike: AbilityDef = {
  id: "heavy-strike",
  name: "Heavy Strike",
  cooldownMs: 4000, // contracts:allow-literal — ability catalog datum
  damageBase: 15, // contracts:allow-literal — ability catalog datum
  damageVariance: 5, // contracts:allow-literal — ability catalog datum
  range: 80, // contracts:allow-literal — ability catalog datum
  gearRequired: "melee",
  skillGate: null,
};

const quickShot: AbilityDef = {
  id: "quick-shot",
  name: "Quick Shot",
  cooldownMs: 2000, // contracts:allow-literal — ability catalog datum
  damageBase: 7, // contracts:allow-literal — ability catalog datum
  damageVariance: 3, // contracts:allow-literal — ability catalog datum
  range: 250, // contracts:allow-literal — ability catalog datum
  gearRequired: "ranged",
  skillGate: null,
};

// Pure buff — Step 9 wires the real damage-reduction effect. damage is
// 0 on the wire so the resolution handler treats it as a cooldown-
// triggering no-op and leaves damage math to the buff subsystem.
const defensiveStance: AbilityDef = {
  id: "defensive-stance",
  name: "Defensive Stance",
  cooldownMs: 10000, // contracts:allow-literal — ability catalog datum
  damageBase: 0,
  damageVariance: 0,
  range: 0,
  gearRequired: null,
  skillGate: { skillId: "crafting", minXp: 100 }, // contracts:allow-literal — ability catalog datum
};

// Heal: damageBase is negative so Step 9's resolution can share the
// single `hp -= damage` path for hits and heals. `damageVariance` still
// applies uniformly, so actual heal ∈ [10, 20].
const healSelf: AbilityDef = {
  id: "heal-self",
  name: "Heal Self",
  cooldownMs: 8000, // contracts:allow-literal — ability catalog datum
  damageBase: -15, // contracts:allow-literal — ability catalog datum
  damageVariance: 5, // contracts:allow-literal — ability catalog datum
  range: 0,
  gearRequired: null,
  skillGate: { skillId: "electronics", minXp: 200 }, // contracts:allow-literal — ability catalog datum
};

// -----------------------------------------------------------------------
// Catalog map + ordered list — mirrors recipes.ts shape.
// -----------------------------------------------------------------------

export const ABILITY_CATALOG: Readonly<Record<AbilityId, AbilityDef>> = {
  "basic-attack": basicAttack,
  "basic-shot": basicShot,
  "heavy-strike": heavyStrike,
  "quick-shot": quickShot,
  "defensive-stance": defensiveStance,
  "heal-self": healSelf,
};

// Display order for the ability bar (4 active slots + basic attack +
// basic ranged alternate). UI decides which subset a given player's
// gear allows; this list is the render order.
export const abilityList: readonly AbilityDef[] = [
  basicAttack,
  basicShot,
  quickShot,
  heavyStrike,
  defensiveStance,
  healSelf,
];

export function getAbility(id: string): AbilityDef | undefined {
  return (ABILITY_CATALOG as Readonly<Record<string, AbilityDef>>)[id];
}

// -----------------------------------------------------------------------
// Gating predicate
// -----------------------------------------------------------------------

// True iff the player can USE the ability — gear matches (strict) AND
// skill gate (if any) satisfied. Does NOT check cooldown or targeting;
// those are runtime concerns for Step 9.
//
// `equippedWeaponType` is the category of the currently-equipped
// weapon, or null if none is equipped. Parallel to the Phase 3 pattern
// of passing a player's skillXpMap into meetsSkillGates.
export function meetsAbilityRequirements(
  ability: AbilityDef,
  skillXpMap: Readonly<Record<string, number>>,
  equippedWeaponType: WeaponCategory | null,
): boolean {
  if (ability.gearRequired !== null && ability.gearRequired !== equippedWeaponType) {
    return false;
  }
  if (ability.skillGate !== null) {
    const have = skillXpMap[ability.skillGate.skillId] ?? 0;
    if (have < ability.skillGate.minXp) return false;
  }
  return true;
}
